import React from 'react'
import axios from 'axios'
import styled from 'styled-components/macro'
import { useHistory } from 'react-router-dom'
import { Input, FormSection } from 'components'

const LayoutWrapper = styled.div`
	max-width: 600px;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
`

const CompanyItem = styled.div`
	padding: 10px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
	background: #fff;
	cursor: pointer;
	outline: 2px solid transparent;
	margin: 1px;
	:hover {
		background: #fafafa;
	}
	&.active {
		outline: 2px solid ${props => props.theme.primaryColor};
		background: #fafafa;
	}
`

const Wrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	@media (max-width: 480px) {
		display: flex;
		flex-wrap: wrap;
	}
`

const Field = styled.div`
	min-width: 200px;
	margin-right: 10px;
	flex-grow: 1;
	grid-column: ${props => (props.wide ? '1 / span 2' : '')};
`

const CompanySearch = () => {
	const history = useHistory()
	const [data, setData] = React.useState([])
	const [query, setQuery] = React.useState('')
	const [loading, setLoading] = React.useState(false)
	const [error, setError] = React.useState(null)
	const [emptyHint, setEmptyHint] = React.useState(null)
	const debouncedQuery = useDebounce(query, 300)

	const fetchData = q => {
		if (!q || q.length < 2) {
			setData([])
			setError(null)
			setEmptyHint(null)
			return
		}
		setLoading(true)
		setError(null)
		setEmptyHint(null)
		axios
			.get(`/api/ytj/search?q=${encodeURIComponent(q)}&limit=10`)
			.then(res => {
				const payload = res.data
				const list = Array.isArray(payload)
					? payload
					: payload?.results || []
				setData(list)
				if (list.length === 0) {
					setEmptyHint(
						'Ei osakeyhtiöitä (OY/OYJ) tällä haulla. Kokeile rekisteröityä toiminimeä tai Y-tunnusta (esim. 1234567-8). Toiminimi-yrittäjät eivät näy YTJ:n avoimessa haussa.'
					)
				}
			})
			.catch(err => {
				setData([])
				setEmptyHint(null)
				setError(
					err?.response?.status === 503
						? 'YTJ on tilapäisesti täynnä. Odota 1–2 minuuttia tai hae Y-tunnuksella.'
						: 'Yrityshaku epäonnistui. Yritä uudelleen.'
				)
			})
			.finally(() => setLoading(false))
	}

	const selectCompany = companyId => {
		axios
			.post(`/api/ytj/companies/${encodeURIComponent(companyId)}/import`)
			.then(() => {
				history.push({
					pathname: '/form/new',
					state: { companyId },
				})
			})
			.catch(err => {
				const msg = err?.response?.data
				setError(
					err?.response?.status === 422 && typeof msg === 'string'
						? msg
						: 'Yrityksen tietojen lataus epäonnistui. Yritä uudelleen.'
				)
			})
	}

	React.useEffect(() => fetchData(debouncedQuery), [debouncedQuery])

	return (
		<LayoutWrapper>
			<FormSection>
		<h1 style={{marginBottom: 0}}>Aloita hakemuksen tekeminen</h1>
		<p style={{marginTop: 0}}>Tarkista yrityksen tiedot ja korjaa tarvittaessa</p>
				<div>
					<Wrapper>
						<Field wide>
							<Input
								value={query}
								onChange={e => {
									setQuery(e.target.value)
								}}
								type="text"
								label={'Syötä yrityksen nimi tai y-tunnus'}
								placeholder="Hae"
							/>
							{loading && <p>Haetaan YTJ:stä…</p>}
							{error && <p style={{ color: '#c00' }}>{error}</p>}
							{!loading && !error && emptyHint && (
								<p style={{ color: '#555' }}>{emptyHint}</p>
							)}
							<div>
								{data.map(item => (
									<CompanyItem
										key={item.id}
										onClick={() => selectCompany(item.id)}
									>
										{item.name} ({item.id})
										{item.companyForm ? ` – ${item.companyForm}` : ''}
										{item.courtName ? (
											<div style={{ fontSize: '0.85em', color: '#555' }}>
												Kotipaikka: {item.hometown || item.city} · {item.courtName}
											</div>
										) : null}
									</CompanyItem>
								))}
							</div>
						</Field>
					</Wrapper>
				</div>
			</FormSection>
		</LayoutWrapper>
	)
}

function useDebounce(value, delay) {
	const [debouncedValue, setDebouncedValue] = React.useState(value)

	React.useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}

export default CompanySearch
